import { z } from 'zod';
import type {
  ResourceTypeFieldAssignment,
  ResourceTypeWorkspaceBinding
} from './resource-type-bindings.api';

const fieldAssignmentBase$ = z.object({
  id: z.string(),
  required: z.boolean().optional().default(false),
  locked: z.boolean().optional().default(false),
  order: z.number().optional().default(0),
  /** Prefer `active`; migrate legacy `archived` when present. */
  active: z.boolean().optional(),
  archived: z.boolean().optional()
});

export const fieldAssignment$ = z
  .union([
    fieldAssignmentBase$.extend({
      source: z.literal('core'),
      coreFieldKey: z.string()
    }),
    fieldAssignmentBase$.extend({
      source: z.literal('custom'),
      customFieldDefinitionId: z.number()
    })
  ])
  .transform((field): ResourceTypeFieldAssignment => {
    const active =
      field.active ?? (field.archived != null ? !field.archived : true);
    if (field.source === 'core') {
      return {
        id: field.id,
        source: 'core',
        coreFieldKey: field.coreFieldKey,
        required: field.required,
        locked: field.locked,
        active,
        order: field.order
      };
    }
    return {
      id: field.id,
      source: 'custom',
      customFieldDefinitionId: field.customFieldDefinitionId,
      required: field.required,
      locked: field.locked,
      active,
      order: field.order
    };
  });

export function parseWorkspaceFields(
  raw: unknown
): ResourceTypeFieldAssignment[] {
  const parsed = z.array(fieldAssignment$).safeParse(raw ?? []);
  return parsed.success ? parsed.data : [];
}

/**
 * Join extras nested on resource types in auth/settings (and GET type graph).
 * Bindings-only — not part of the public {@link ResourceType} model.
 */
export const resourceTypeWorkspaceRelation$ = z
  .object({
    id: z.number(),
    enabled: z.boolean().optional(),
    workspace_metastructure: z.unknown().optional()
  })
  .passthrough();

export type ResourceTypeWorkspaceRelation = z.infer<
  typeof resourceTypeWorkspaceRelation$
>;

/** Shape used only when flattening settings/Redux join extras into bindings. */
export const resourceTypeBindingSourceRow$ = z
  .object({
    id: z.number(),
    enterprise_workspaces: z.array(resourceTypeWorkspaceRelation$).optional()
  })
  .passthrough();

export type ResourceTypeBindingSourceRow = z.infer<
  typeof resourceTypeBindingSourceRow$
>;

/**
 * Read join extras from v1 Redux `resource_types.data`.
 * Missing `resource_types` / `data` → `[]` (auth not loaded yet).
 * Malformed rows or `resource_types: null` → throw (we sync this bag; fail loud).
 */
export function parseBindingSourceRowsFromRedux(
  raw: unknown
): ResourceTypeBindingSourceRow[] {
  const data = z
    .object({
      resource_types: z
        .object({
          data: z.record(z.string(), resourceTypeBindingSourceRow$).optional()
        })
        .optional()
    })
    .passthrough()
    .parse(raw);

  return Object.values(data.resource_types?.data ?? {});
}

export const workspaceBindingJoinRow$ = z
  .object({
    resource_type_id: z.number(),
    enterprise_workspace_id: z.number(),
    enabled: z.boolean(),
    workspace_metastructure: z.unknown().nullable().optional()
  })
  .passthrough();

export function bindingFromJoinRow(
  row: z.infer<typeof workspaceBindingJoinRow$>
): ResourceTypeWorkspaceBinding {
  return {
    resourceTypeId: row.resource_type_id,
    workspaceId: row.enterprise_workspace_id,
    enabled: row.enabled,
    workspaceFields: parseWorkspaceFields(row.workspace_metastructure)
  };
}

export function parseWorkspaceBindingJoinRow(
  raw: unknown
): ResourceTypeWorkspaceBinding {
  return bindingFromJoinRow(workspaceBindingJoinRow$.parse(raw));
}

export function parseWorkspaceRelationsFromTypeHttp(
  raw: unknown
): ResourceTypeWorkspaceRelation[] {
  const parsed = z
    .object({
      enterprise_workspaces: z
        .array(resourceTypeWorkspaceRelation$)
        .optional()
        .default([])
    })
    .passthrough()
    .parse(raw);
  return parsed.enterprise_workspaces;
}

export function flattenResourceTypeBindings(
  types: ResourceTypeBindingSourceRow[]
): ResourceTypeWorkspaceBinding[] {
  return types.flatMap(type =>
    (type.enterprise_workspaces ?? []).map(workspace => ({
      resourceTypeId: type.id,
      workspaceId: workspace.id,
      enabled: workspace.enabled ?? true,
      workspaceFields: parseWorkspaceFields(workspace.workspace_metastructure)
    }))
  );
}
